import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useNavbar } from '../contexts/NavbarContext';
import './Blog.css';

// AI-powered table of contents generator
const generateTableOfContents = async (content) => {
  try {
    // Check if API key exists
    if (!process.env.REACT_APP_GEMINI_API_KEY || process.env.REACT_APP_GEMINI_API_KEY === 'your_gemini_api_key_here') {
      console.log('Using fallback TOC generation - no valid API key');
      return generateFallbackTOC(content);
    }

    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: `Create a relevant table of contents for this blog post. Focus on the actual content and topic. If content is short, create logical sections that would naturally follow from the topic. Return 1-4 numbered sections. Use plain text only, no markdown formatting.

Return format:
1. Section Title
2. Another Section (if needed)
3. Another Section (if needed)
4. Final Section (if needed)

Blog content:
${content}

IMPORTANT: Create sections that are directly relevant to the content/topic. Don't make generic sections. Minimum 1 section. Use plain text only.`
          }]
        }]
      })
    });

    const data = await response.json();
    
    if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts[0]) {
      const tocText = data.candidates[0].content.parts[0].text;
      // Parse the numbered list into sections
      const sections = tocText.split('\n')
        .filter(line => line.match(/^\d+\./))
        .map(line => {
          const match = line.match(/^\d+\.\s*(.+)/);
          return match ? match[1].trim() : line.trim();
        })
        .filter(section => section.length > 0);
      
      return sections;
    }
  } catch (error) {
    console.error('Error generating TOC:', error);
  }
  
  // Enhanced markdown heading extraction
  return extractMarkdownHeadings(content);
};

// Enhanced markdown heading extractor
const extractMarkdownHeadings = (content) => {
  // Extract all markdown headings (H1, H2, H3, H4, H5, H6)
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const headings = [];
  let match;
  
  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length; // Number of # symbols
    const title = match[2].trim();
    
    // Remove markdown formatting from title (bold, italic, code, etc.)
    const cleanTitle = title
      .replace(/\*\*(.*?)\*\*/g, '$1') // **bold**
      .replace(/\*(.*?)\*/g, '$1')     // *italic*
      .replace(/`(.*?)`/g, '$1')       // `code`
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // [link](url)
      .replace(/~~(.*?)~~/g, '$1')     // ~~strikethrough~~
      .replace(/^#+\s*/, '')           // Remove any remaining # symbols
      .trim();
    
    headings.push({
      level,
      title: cleanTitle,
      original: title
    });
  }
  
  if (headings.length > 0) {
    // Sort by level and return titles
    return headings
      .sort((a, b) => a.level - b.level)
      .map(heading => heading.title);
  }
  
  // If no headings found, create default sections based on content
  const defaultSections = [
    'Introduction',
    'Main Content',
    'Key Points',
    'Conclusion'
  ];
  
  // If content is very short, create more creative sections
  if (content.length < 100) {
    return [
      'Getting Started',
      'Core Concepts',
      'Practical Applications',
      'Next Steps'
    ];
  }
  
  return defaultSections;
};

// Better fallback TOC generator
const generateFallbackTOC = (content) => {
  // Extract meaningful words from content
  const words = content.toLowerCase()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter(word => word.length > 2);
  
  // Create relevant sections based on content
  if (content.toLowerCase().includes('test')) {
    return [
      'What We\'re Testing',
      'Test Methodology', 
      'Expected Results',
      'Test Conclusion'
    ];
  }
  
  if (content.toLowerCase().includes('blog')) {
    return [
      'Blog Overview',
      'Main Discussion',
      'Key Takeaways',
      'Further Reading'
    ];
  }
  
  // For very short content, just return 1 section
  if (content.length < 50) {
    return ['Content Overview'];
  }
  
  // Generic but relevant sections
  return [
    'Overview',
    'Main Points',
    'Important Details',
    'Summary'
  ];
};

// Language options for translation
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' }
];

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [tableOfContents, setTableOfContents] = useState([]);
  const [isGeneratingTOC, setIsGeneratingTOC] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentAuthor, setCommentAuthor] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const { hideNavbar, showNavbar } = useNavbar();

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (selectedPost) {
      hideNavbar();
    } else {
      showNavbar();
    }
  }, [selectedPost, hideNavbar, showNavbar]);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const translateContent = async (content, targetLanguage) => {
    if (targetLanguage === 'en') {
      setTranslatedContent(null);
      return;
    }

    setIsTranslating(true);
    try {
      // Using Gemini AI for high-quality translation
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Translate the following English text to ${LANGUAGES.find(lang => lang.code === targetLanguage)?.name}. Maintain the original formatting, tone, and style. Only return the translated text, nothing else:\n\n${content}`
            }]
          }]
        })
      });

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts[0]) {
        setTranslatedContent(data.candidates[0].content.parts[0].text);
      } else {
        throw new Error('Invalid response from Gemini API');
      }
    } catch (error) {
      console.error('Translation error:', error);
      // Fallback to free translation service if Gemini fails
      try {
        const fallbackResponse = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(content)}&langpair=en|${targetLanguage}`);
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.responseStatus === 200) {
          setTranslatedContent(fallbackData.responseData.translatedText);
        } else {
          setTranslatedContent(null);
        }
      } catch (fallbackError) {
        console.error('Fallback translation error:', fallbackError);
        setTranslatedContent(null);
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const handleLanguageChange = (languageCode) => {
    setSelectedLanguage(languageCode);
    setShowLanguageDropdown(false);
    if (selectedPost) {
      translateContent(selectedPost.content, languageCode);
    }
  };

  const handlePostSelect = async (post) => {
    setSelectedPost(post);
    setTableOfContents([]);
    setTranslatedContent(null);
    setSelectedLanguage('en');
    setNewComment('');
    setCommentAuthor('');
    setReplyingTo(null);
    setReplyContent('');
    
    // Generate table of contents using AI
    setIsGeneratingTOC(true);
    try {
      const toc = await generateTableOfContents(post.content);
      setTableOfContents(toc);
      
      // Fetch comments for this post
      await fetchComments(post.id);
    } catch (error) {
      console.error('Error generating TOC:', error);
    } finally {
      setIsGeneratingTOC(false);
    }
  };

  const scrollToSection = (sectionTitle) => {
    const content = document.querySelector('.post-body');
    if (content) {
      const text = content.textContent;
      const sectionIndex = text.indexOf(sectionTitle);
      if (sectionIndex !== -1) {
        content.scrollTo({
          top: sectionIndex / 10, // Approximate scroll position
          behavior: 'smooth'
        });
      }
    }
  };

  // Fetch comments for a specific post
  const fetchComments = async (postId) => {
    try {
      // Fetch comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('blog_comments')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      // Fetch replies for each comment
      const commentsWithReplies = await Promise.all(
        (commentsData || []).map(async (comment) => {
          const { data: repliesData, error: repliesError } = await supabase
            .from('blog_comment_replies')
            .select('*')
            .eq('comment_id', comment.id)
            .order('created_at', { ascending: true });

          if (repliesError) {
            console.error('Error fetching replies:', repliesError);
            return { ...comment, replies: [] };
          }

          return { ...comment, replies: repliesData || [] };
        })
      );

      setComments(commentsWithReplies);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    }
  };

  // Submit a new comment
  const handleSubmitComment = async () => {
    if (!newComment.trim() || !selectedPost) return;

    setIsSubmittingComment(true);
    try {
      const { data, error } = await supabase
        .from('blog_comments')
        .insert([
          {
            post_id: selectedPost.id,
            content: newComment.trim(),
            author_name: commentAuthor.trim() || 'Anonymous',
            created_at: new Date().toISOString()
          }
        ])
        .select();

      if (error) throw error;

      // Add the new comment to the list
      setComments(prev => [...prev, data[0]]);
      setNewComment('');
      setCommentAuthor('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Handle reply button click
  const handleReplyClick = (commentId, authorName) => {
    setReplyingTo(commentId);
    setReplyContent('');
  };

  // Submit a reply to a comment
  const handleSubmitReply = async (commentId) => {
    if (!replyContent.trim() || !selectedPost) return;

    setIsSubmittingReply(true);
    try {
      const { data, error } = await supabase
        .from('blog_comment_replies')
        .insert([
          {
            comment_id: commentId,
            post_id: selectedPost.id,
            content: replyContent.trim(),
            author_name: commentAuthor.trim() || 'Anonymous',
            created_at: new Date().toISOString()
          }
        ])
        .select();

      if (error) throw error;

      // Add the reply to the comment
      setComments(prev => prev.map(comment => {
        if (comment.id === commentId) {
          return {
            ...comment,
            replies: [...(comment.replies || []), data[0]]
          };
        }
        return comment;
      }));

      setReplyContent('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <section className="hero-section">
          <div className="hero-background">
            <div className="hero-gradient"></div>
            <div className="hero-grid-pattern"></div>
            <div className="hero-orbs">
              <div className="hero-orb hero-orb-1"></div>
              <div className="hero-orb hero-orb-2"></div>
              <div className="hero-orb hero-orb-3"></div>
            </div>
          </div>
          <div className="hero-content">
            <h1 className="hero-title">Blog</h1>
            <p className="hero-description">Loading latest insights...</p>
          </div>
        </section>

        <section className="section">
          <div className="floating-stars">
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
          </div>
          <div className="container">
            <div className="blog-loading">
              <div className="loading-spinner"></div>
              <p>Loading blog posts...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (selectedPost) {
    return (
      <div className="blog-post-full">
        <div className="container">
          <div className="back-button-container">
            <button 
              className="back-button"
              onClick={() => setSelectedPost(null)}
            >
              ← Back to Blog
            </button>
            
            {/* Language Selector */}
            <div className="language-selector">
              <button 
                className="language-button"
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <span className="language-flag">{LANGUAGES.find(lang => lang.code === selectedLanguage)?.flag}</span>
                <span className="language-name">{LANGUAGES.find(lang => lang.code === selectedLanguage)?.name}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              
              {showLanguageDropdown && (
                <div className="language-dropdown">
                  {LANGUAGES.map((language) => (
                    <button
                      key={language.code}
                      className={`language-option ${selectedLanguage === language.code ? 'active' : ''}`}
                      onClick={() => handleLanguageChange(language.code)}
                    >
                      <span className="language-flag">{language.flag}</span>
                      <span className="language-name">{language.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          

          
          <div className="blog-post-layout">
            <article className="blog-post-content">
              <header className="post-header">
                <h1 className="post-title">{selectedPost.title}</h1>
                <div className="post-meta">
                  <span className="post-date">{formatDate(selectedPost.created_at)}</span>
                  {selectedPost.author && (
                    <span className="post-author">by {selectedPost.author}</span>
                  )}
                </div>
              </header>
              
              <div className="post-body">
              {translatedContent ? (
                <div className="translated-content">
                  <div className="translation-header">
                    <span className="translation-badge">
                      🌐 {LANGUAGES.find(lang => lang.code === selectedLanguage)?.name}
                    </span>
                    <button 
                      className="show-original-btn"
                      onClick={() => {
                        setTranslatedContent(null);
                        setSelectedLanguage('en');
                      }}
                    >
                      Show Original
                    </button>
                  </div>
                  <div className="translated-text">
                    {translatedContent}
                  </div>
                </div>
              ) : (
                <ReactMarkdown
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={tomorrow}
                          language={match[1]}
                          Pretag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {selectedPost.content}
                </ReactMarkdown>
              )}
            </div>
          </article>
          
          {/* Right Side Panel - Table of Contents */}
          <aside className="toc-sidebar">
            <div className="toc-header">
              <h3>Table of Contents</h3>
              {isGeneratingTOC && <span className="toc-loading">🤖 AI generating...</span>}
            </div>
            
            {tableOfContents.length > 0 && (
              <nav className="toc-nav">
                {tableOfContents.map((section, index) => (
                  <button
                    key={index}
                    className="toc-item"
                    onClick={() => scrollToSection(section)}
                  >
                    <span className="toc-number">{index + 1}.</span>
                    <span className="toc-title">{section}</span>
                  </button>
                ))}
              </nav>
            )}
            
            {!isGeneratingTOC && tableOfContents.length === 0 && (
              <div className="toc-empty">
                <p>No sections found</p>
              </div>
            )}
          </aside>
        </div>
        
        {/* Enhanced Comments Section */}
        <div className="comments-section">
          <div className="comments-header">
            <h3 className="comments-title">
              <svg className="comments-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Discussion ({comments.length})
            </h3>
            <p className="comments-subtitle">Join the conversation and share your thoughts</p>
          </div>
          
          {/* Enhanced Comment Form */}
          <div className="comment-form">
            <div className="comment-form-header">
              <div className="author-input-group">
                <input
                  type="text"
                  className="author-input"
                  placeholder="Your name (optional)"
                  value={commentAuthor}
                  onChange={(e) => setCommentAuthor(e.target.value)}
                />
              </div>
            </div>
            <textarea
              className="comment-input"
              placeholder="What are your thoughts on this article? Share your insights, questions, or experiences..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows="4"
              maxLength="1000"
            />
            <div className="comment-form-footer">
              <div className="comment-char-count">
                {newComment.length}/1000 characters
              </div>
              <button 
                className="comment-submit-btn"
                onClick={handleSubmitComment}
                disabled={isSubmittingComment || !newComment.trim()}
              >
                                                {isSubmittingComment ? (
                                  <>
                                    <span className="loading-spinner"></span>
                                    Posting...
                                  </>
                                ) : (
                                  <>
                                    <svg className="comment-icon" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                    </svg>
                                    Post Comment
                                  </>
                                )}
              </button>
            </div>
          </div>
          
          {/* Enhanced Comments List */}
          <div className="comments-list">
            {comments.length === 0 ? (
              <div className="no-comments">
                <div className="no-comments-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h4>No comments yet</h4>
                <p>Be the first to start the conversation! Share your thoughts, ask questions, or share your experiences.</p>
              </div>
            ) : (
              <>
                <div className="comments-stats">
                  <span className="comments-count">{comments.length} comment{comments.length !== 1 ? 's' : ''}</span>
                  <span className="comments-sort">Newest first</span>
                </div>
                {comments.map((comment) => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-avatar">
                      <span className="avatar-text">
                        {(comment.author_name || 'A').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="comment-content-wrapper">
                      <div className="comment-header">
                        <div className="comment-author-info">
                          <span className="comment-author">{comment.author_name || 'Anonymous'}</span>
                          <span className="comment-badge">Reader</span>
                        </div>
                        <div className="comment-meta">
                          <span className="comment-date">{formatDate(comment.created_at)}</span>
                          {comment.updated_at !== comment.created_at && (
                            <span className="comment-edited">(edited)</span>
                          )}
                        </div>
                      </div>
                      <div className="comment-content">{comment.content}</div>
                      <div className="comment-actions">
                        <button 
                          className="comment-action-btn reply-btn"
                          onClick={() => handleReplyClick(comment.id, comment.author_name || 'Anonymous')}
                        >
                          <img src="https://www.svgrepo.com/show/18854/reply.svg" alt="Reply" className="action-icon" />
                          <span className="action-text">Reply</span>
                        </button>
                      </div>
                      
                      {/* Reply Form */}
                      {replyingTo === comment.id && (
                        <div className="reply-form">
                          <div className="reply-form-header">
                            <span className="reply-to">Replying to {comment.author_name || 'Anonymous'}</span>
                            <button 
                              className="cancel-reply-btn"
                              onClick={() => setReplyingTo(null)}
                            >
                              <svg className="cancel-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                              </svg>
                            </button>
                          </div>
                          <textarea
                            className="reply-input"
                            placeholder="Write your reply..."
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            rows="3"
                            maxLength="500"
                          />
                          <div className="reply-form-footer">
                            <div className="reply-char-count">
                              {replyContent.length}/500 characters
                            </div>
                            <button 
                              className="reply-submit-btn"
                              onClick={() => handleSubmitReply(comment.id)}
                              disabled={isSubmittingReply || !replyContent.trim()}
                            >
                              {isSubmittingReply ? (
                                <>
                                  <span className="loading-spinner"></span>
                                  Posting Reply...
                                </>
                              ) : (
                                <>
                                  <img src="https://www.svgrepo.com/show/18854/reply.svg" alt="Reply" className="reply-icon" />
                                  Post Reply
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Show Replies */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="comment-replies">
                          {comment.replies.map((reply) => (
                            <div key={reply.id} className="reply-item">
                              <div className="reply-avatar">
                                <span className="avatar-text">
                                  {(reply.author_name || 'A').charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="reply-content-wrapper">
                                <div className="reply-header">
                                  <div className="reply-author-info">
                                    <span className="reply-author">{reply.author_name || 'Anonymous'}</span>
                                    <span className="reply-badge">Reader</span>
                                  </div>
                                  <div className="reply-meta">
                                    <span className="reply-date">{formatDate(reply.created_at)}</span>
                                  </div>
                                </div>
                                <div className="reply-content">{reply.content}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  }

  return (
    <div className="main-content">
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid-pattern"></div>
          <div className="hero-orbs">
            <div className="hero-orb hero-orb-1"></div>
            <div className="hero-orb hero-orb-2"></div>
            <div className="hero-orb hero-orb-3"></div>
          </div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">Blog</h1>
          <p className="hero-description">Latest insights, updates, and stories from our team</p>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          {posts.length === 0 ? (
            <div className="no-posts">
              <p>No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="blog-grid">
              {posts.map((post) => (
                <article 
                  key={post.id} 
                  className="blog-card"
                  onClick={() => handlePostSelect(post)}
                >
                  <div className="card-content">
                    <h2 className="card-title">{post.title}</h2>
                    <p className="card-excerpt">
                      {post.excerpt || post.content.substring(0, 150)}...
                    </p>
                    <div className="card-meta">
                      <span className="card-date">{formatDate(post.created_at)}</span>
                      {post.author && (
                        <span className="card-author">{post.author}</span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
